import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { Loader2, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { AdminApi } from "@/api";
import { TONE_CLASS } from "@/components/batch-test/batchCells";
import { modelLabel, shortSha, teName } from "@/components/batch-test/teSpec";
import { lastSearch, rememberSearch } from "@/components/layout/lastSearch";
import { ALL_TEST_CASES_DEFAULT_SEARCH, ALL_TEST_CASES_ROUTE } from "@/components/test-cases/allTestCasesSearch";
import { testCaseGate } from "@/components/test-cases/prodCase";
import { feedbackCountText } from "@/components/test-cases/sourceStatus";
import { Badge, Banner, Button, Card, CardHead, IconButton, Skeleton } from "@/components/ui";
import { cn } from "@/lib/utils";
import type { BatchPageDetail, BatchPageRow, BatchPageTestCase, TeBaseline, TestCaseDetail } from "@/types";
import { compactTime } from "@/utils/format";
import { formatScore } from "@/utils/rubric";
import { sessionTypeLabel } from "@/utils/sessionType";
import { matrixColumns, summarizePage } from "./matrix";
import { MatrixTable, tcShort } from "./MatrixTable";
import { PagelessRuns } from "./PagelessRuns";
import { codeOptions, columnMatches, effectiveModel, pageMatches, specMatches } from "./pageFilter";
import { rememberShownPage } from "./shownPage";
import { SCORE_GRADIENT } from "./scoreColor";
import {
  DAYS_OPTIONS,
  filtersActive,
  type ResultDays,
  TEST_RESULT_ROUTE,
  type TestResultSearch,
} from "./testResultSearch";

/** Enough for every page this admin has made; the list is not paged here. */
const PAGE_LIMIT = 100;

const SELECT =
  "rounded-md border border-border bg-surface-input px-2 py-1 text-xs disabled:cursor-not-allowed disabled:opacity-60";

function errorText(err: unknown) {
  return err instanceof Error ? err.message : String(err);
}

/**
 * The page the Matrix shows. An explicit `?page` wins while the filters
 * still allow it; `?tc` alone means "the newest page that holds this TC and
 * has run"; otherwise the newest page that has run. A default is not
 * written into the URL — it is not a choice anyone made.
 */
function choosePage(
  matched: readonly BatchPageRow[],
  search: TestResultSearch,
  holding: readonly string[] | null,
): BatchPageRow | null {
  if (search.page) {
    const hit = matched.find((r) => r.page_id === search.page);
    if (hit) return hit;
  }
  if (search.tc && holding) {
    const pages = matched.filter((r) => holding.includes(r.page_id));
    const pick = pages.find((r) => r.run_count > 0) ?? pages[0];
    if (pick) return pick;
  }
  return matched.find((r) => r.run_count > 0) ?? matched[0] ?? null;
}

/**
 * One page in the picker, as the artifact writes it:
 * `BT-01 · 이름 — 09-28 14:02 · 평균 0.72`. The mean is the Matrix's own
 * (BE works it out the same way), so picking a page never changes the number.
 */
function pageOptionLabel(r: BatchPageRow): string {
  const when = r.last_run ? compactTime(r.last_run.created_at) : "실행 전";
  // An older BE sends no mean at all — say nothing rather than "—".
  const mean = r.avg === undefined ? "" : ` · 평균 ${r.avg != null ? formatScore(r.avg) : "—"}`;
  return `${r.label} · ${r.name} — ${when}${mean}`;
}

/**
 * Test Result — the TC × TE Matrix of one Batch Test page
 * (fe/TEST_CASES_RESULT.md §3; artifact "Test Result").
 *
 * Batch Test starts runs; this reads them. The filters narrow which pages
 * can be picked; the Matrix itself always shows every TE the page ran, and
 * the ones the TE filters do not match are faded rather than hidden.
 *
 * `?page` · `?tc` · `?model` · `?code` · `?days` are URL state (hub §4.2):
 * a filtered Matrix with a row open is a link. Opening a cell is a move
 * (push) to that cell's Test Result.
 */
export function TestResultPage({ api, reloadKey = 0 }: { api: AdminApi; reloadKey?: number }) {
  const search = useSearch({ from: TEST_RESULT_ROUTE });
  const navigate = useNavigate();

  // The sidebar and the result screens come back to this search.
  useEffect(() => {
    rememberSearch(TEST_RESULT_ROUTE, search);
  }, [search]);

  const [baseline, setBaseline] = useState<TeBaseline | null>(null);
  const [rows, setRows] = useState<BatchPageRow[] | null>(null);
  // "최근 N 일" is measured from when the list was read, not re-evaluated
  // on every render.
  const [loadedAt, setLoadedAt] = useState(() => new Date());
  const [error, setError] = useState("");
  const [holding, setHolding] = useState<{ tc: string; pageIds: string[] } | null>(null);
  const [detail, setDetail] = useState<{ id: string; page?: BatchPageDetail; error?: string } | null>(
    null,
  );

  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const [base, list] = await Promise.all([
          api.getTeBaseline().catch(() => null),
          api.listBatchPages({ limit: PAGE_LIMIT }),
        ]);
        if (!alive) return;
        setBaseline(base);
        setRows(list.items);
        setLoadedAt(new Date());
      } catch (err) {
        if (alive) setError(errorText(err));
      }
    })();
    return () => {
      alive = false;
    };
  }, [api, reloadKey]);

  // Which pages hold the open TC — BE answers from the GIN index rather
  // than every page's detail being read here.
  useEffect(() => {
    if (!search.tc) return;
    const tc = search.tc;
    let alive = true;
    api
      .listBatchPages({ limit: PAGE_LIMIT, testCaseId: tc })
      .then((res) => alive && setHolding({ tc, pageIds: res.items.map((r) => r.page_id) }))
      .catch(() => alive && setHolding({ tc, pageIds: [] }));
    return () => {
      alive = false;
    };
  }, [api, search.tc]);

  const defaultModel = baseline?.default_model ?? null;
  const matched = useMemo(
    () => (rows ?? []).filter((r) => pageMatches(r, search, defaultModel, loadedAt)),
    [rows, search, defaultModel, loadedAt],
  );
  const holdingIds = holding && holding.tc === search.tc ? holding.pageIds : null;
  // With only `?tc`, wait for the pages that hold it rather than showing a
  // default Matrix and then jumping.
  const ready = rows !== null && (!search.tc || !!search.page || holdingIds !== null);
  const chosen = ready ? choosePage(matched, search, holdingIds) : null;
  const chosenId = chosen?.page_id ?? null;

  // Batch Test labels this page "Matrix 표시 중" — whether the URL named it
  // or the default picked it.
  useEffect(() => {
    if (chosenId) rememberShownPage(chosenId);
  }, [chosenId]);

  useEffect(() => {
    if (!chosenId) return;
    let alive = true;
    setDetail({ id: chosenId });
    api
      .getBatchPage(chosenId)
      .then((page) => alive && setDetail({ id: chosenId, page }))
      .catch((err) => alive && setDetail({ id: chosenId, error: errorText(err) }));
    return () => {
      alive = false;
    };
  }, [api, chosenId, reloadKey]);

  const page = detail?.id === chosenId ? detail.page : undefined;
  const pageError = detail?.id === chosenId ? detail.error : undefined;

  // --- URL changes ---------------------------------------------------------

  const updateSearch = useCallback(
    (patch: Partial<TestResultSearch>, opts: { replace?: boolean } = {}) => {
      navigate({ to: TEST_RESULT_ROUTE, search: { ...search, ...patch }, replace: opts.replace });
    },
    [navigate, search],
  );

  const toggleRow = (tc: string) => {
    if (search.tc === tc) updateSearch({ tc: null }, { replace: true });
    else updateSearch({ tc, page: chosenId });
  };

  const openCell = (runId: string, tc: string) =>
    navigate({ to: "/test-cases/result/runs/$runId/$tcId", params: { runId, tcId: tc } });

  // --- Derived ---------------------------------------------------------------

  const columns = useMemo(() => (page ? matrixColumns(page) : []), [page]);
  const dimmed = useMemo(
    () =>
      new Set(
        columns
          .filter((c) => !columnMatches(c.spec, c.isBaseline, search, defaultModel))
          .map((c) => c.key),
      ),
    [columns, search, defaultModel],
  );
  const summary = useMemo(() => (page ? summarizePage(page) : null), [page]);
  const active = filtersActive(search);
  const running = page?.runs.find((r) => r.status === "running");

  const modelChoices = useMemo(() => {
    const ids = new Set((baseline?.models ?? []).map((m) => m.id));
    // A model only an old run used is still a way to find that run.
    for (const row of rows ?? []) {
      for (const spec of row.te_specs) ids.add(effectiveModel(spec ?? {}, defaultModel));
    }
    return [...ids].filter(Boolean);
  }, [baseline, rows, defaultModel]);
  const codeChoices = useMemo(() => codeOptions(rows ?? []), [rows]);

  // The TEs that match — not the baseline just because it never fades.
  const matchedTeNames =
    page && (search.model || search.code)
      ? columns
          .filter((c) => specMatches(c.spec, search, defaultModel))
          .map((c) => `${c.letter} ${teName(c.spec, baseline)}`)
      : [];

  return (
    <div className={cn("grid items-start gap-5", search.tc && "lg:grid-cols-[minmax(0,1fr)_360px]")}>
      <div className="grid min-w-0 gap-3">
        {error && <Banner tone="error">{error}</Banner>}

        <Card className="min-w-0 overflow-hidden">
          <CardHead title="TC × TE Matrix" hint="셀 = Final(0~1, rubric v2) · 클릭하면 Test Result">
            <span className="text-xs text-muted-foreground tabular-nums">
              {matched.length} / {rows?.length ?? 0} Batch Test{active && " · 필터 적용 중"}
            </span>
            <Button
              size="sm"
              variant="ghost"
              disabled={!active}
              onClick={() => updateSearch({ model: "", code: "", days: "", tc: null })}
            >
              필터 해제
            </Button>
          </CardHead>

          <div className="grid gap-2 border-b border-border bg-muted/30 px-4 py-3">
            <div className="flex flex-wrap items-center gap-2">
              <select
                aria-label="모델"
                className={SELECT}
                value={search.model}
                onChange={(e) => updateSearch({ model: e.target.value, tc: null })}
              >
                <option value="">모델 전체</option>
                {modelChoices.map((id) => (
                  <option key={id} value={id}>
                    모델 {modelLabel(id, baseline)}
                    {id === defaultModel ? " (배포본)" : ""}
                  </option>
                ))}
              </select>
              {/* Prompt versions are fixed by snapshot_at today, so a run
                  cannot be told apart by them (BE-REPLAY-PROMPT-PIN). */}
              <select aria-label="Main Agent 프롬프트" className={SELECT} disabled>
                <option>Main Agent — Replay Lab 확장 대기</option>
              </select>
              <select aria-label="Subagent 프롬프트" className={SELECT} disabled>
                <option>Subagent — Replay Lab 확장 대기</option>
              </select>
              <select
                aria-label="코드"
                className={SELECT}
                value={search.code}
                onChange={(e) => updateSearch({ code: e.target.value, tc: null })}
              >
                <option value="">코드 전체</option>
                <option value="deployment">
                  코드 {baseline?.deployed_sha ? `${shortSha(baseline.deployed_sha)} ` : ""}(배포본)
                </option>
                {codeChoices.map((ref) => (
                  <option key={ref} value={ref}>
                    코드 {shortSha(ref)}
                  </option>
                ))}
              </select>
              <select aria-label="TC 도메인" className={SELECT} disabled>
                <option>도메인 — BE-DOMAIN 대기</option>
              </select>
              <select
                aria-label="기간"
                className={SELECT}
                value={search.days}
                onChange={(e) => updateSearch({ days: e.target.value as ResultDays, tc: null })}
              >
                {DAYS_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-wrap items-center gap-2 text-xs">
              <span className="text-muted-foreground">결과</span>
              <select
                aria-label="표시할 Batch Test"
                className={cn(SELECT, "max-w-full")}
                value={chosenId ?? ""}
                disabled={matched.length === 0}
                onChange={(e) => updateSearch({ page: e.target.value || null, tc: null })}
              >
                {matched.length === 0 ? (
                  <option value="">조건에 맞는 결과 없음</option>
                ) : (
                  matched.map((r) => (
                    <option key={r.page_id} value={r.page_id}>
                      {pageOptionLabel(r)}
                    </option>
                  ))
                )}
              </select>
              {page && (
                <span className="text-muted-foreground">
                  {[
                    page.created_by_email ?? "—",
                    page.runs[0] ? `마지막 ${compactTime(page.runs[0].created_at)}` : "실행 전",
                    `${page.test_case_ids.length} TC`,
                    `실행 ${page.runs.length} 회`,
                    `평균 ${summary?.avg != null ? formatScore(summary.avg) : "—"}`,
                    ...(matchedTeNames.length ? [`조건에 맞는 실행 — ${matchedTeNames.join(" · ")}`] : []),
                  ].join("  ·  ")}
                </span>
              )}
              {page && page.runs.length > 0 && (
                <span className="ml-auto flex gap-1">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() =>
                      navigate({
                        to: "/test-cases/result/pages/$pageId/summary",
                        params: { pageId: page.page_id },
                      })
                    }
                  >
                    결과 정리 보기
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() =>
                      navigate({ to: "/test-cases/batch/$pageId", params: { pageId: page.page_id } })
                    }
                  >
                    Batch Test 에서 열기
                  </Button>
                </span>
              )}
            </div>
          </div>

          {running && page && (
            <p className="border-b border-border px-4 py-2 text-xs text-muted-foreground" role="status">
              {matrixColumns(page).find((c) => c.run.batch_run_id === running.batch_run_id)?.letter ??
                "TE"}{" "}
              실행 중 — 끝나면 이 열이 채워집니다. 진행은 Batch Test 에서 볼 수 있습니다.
            </p>
          )}

          <MatrixBody
            rows={rows}
            matched={matched}
            active={active}
            page={page}
            pageError={pageError}
            chosen={chosen}
          >
            {page && (
              <MatrixTable
                page={page}
                columns={columns}
                baseline={baseline}
                selectedTc={search.tc}
                dimmed={dimmed}
                onRow={toggleRow}
                onCell={(column, tc) => openCell(column.run.batch_run_id, tc)}
              />
            )}
          </MatrixBody>

          <Legend note={active && (search.model || search.code) ? "filtered" : "default"} />
        </Card>

        <PagelessRuns api={api} baseline={baseline} reloadKey={reloadKey} onCell={openCell} />
      </div>

      {search.tc && (
        <TcPanel
          api={api}
          testCaseId={search.tc}
          inPage={page?.test_cases.find((t) => t.test_case_id === search.tc) ?? null}
          pageReady={!!page || (ready && !chosen)}
          holding={holdingIds}
          onClose={() => updateSearch({ tc: null }, { replace: true })}
          onShowPage={(pageId) => updateSearch({ page: pageId })}
        />
      )}
    </div>
  );
}

function MatrixBody({
  rows,
  matched,
  active,
  page,
  pageError,
  chosen,
  children,
}: {
  rows: BatchPageRow[] | null;
  matched: BatchPageRow[];
  active: boolean;
  page: BatchPageDetail | undefined;
  pageError: string | undefined;
  chosen: BatchPageRow | null;
  children: React.ReactNode;
}) {
  const empty = (body: React.ReactNode) => (
    <p className="px-4 py-10 text-center text-sm text-muted-foreground">{body}</p>
  );
  if (rows === null) {
    return (
      <div className="grid gap-2 p-4">
        {Array.from({ length: 4 }, (_, i) => (
          <Skeleton key={i} className="h-10" />
        ))}
      </div>
    );
  }
  if (rows.length === 0) {
    return empty(
      <>
        표시할 Batch Test 가 없습니다. <Link to="/test-cases/batch" className="underline">Batch Test</Link> 탭에서
        페이지를 만들고 실행하면 결과가 여기에 나타납니다.
      </>,
    );
  }
  if (matched.length === 0) {
    return empty(
      active
        ? "이 조건으로 돌린 Batch Test 가 없습니다. 위에서 필터를 줄여 보세요."
        : "표시할 Batch Test 가 없습니다.",
    );
  }
  if (pageError) return <div className="p-4"><Banner tone="error">{pageError}</Banner></div>;
  if (!page) {
    return (
      <div className="grid gap-2 p-4">
        {Array.from({ length: 3 }, (_, i) => (
          <Skeleton key={i} className="h-10" />
        ))}
      </div>
    );
  }
  if (page.runs.length === 0) {
    return empty(
      <>
        {chosen?.label ?? page.label} 에서 아직 실행한 TE 가 없습니다.{" "}
        <Link to="/test-cases/batch/$pageId" params={{ pageId: page.page_id }} className="underline">
          Batch Test
        </Link>{" "}
        에서 이 페이지를 열어 TE 를 골라 실행하세요.
      </>,
    );
  }
  return <>{children}</>;
}

const LEGEND: [keyof typeof TONE_CLASS, string][] = [
  ["na", "평가 불가 · Reference 없음"],
  ["failed", "실패"],
  ["unscored", "채점 대기"],
  ["legacy", "v1 · 집계 제외"],
];

function Legend({ note }: { note: "default" | "filtered" }) {
  return (
    <div className="flex flex-wrap items-center gap-3 border-t border-border px-4 py-2 text-[11px] text-muted-foreground">
      <span className="flex items-center gap-1.5">
        Score 0
        <span aria-hidden className="inline-block h-3 w-24 rounded-sm" style={{ background: SCORE_GRADIENT }} />
        1
      </span>
      {LEGEND.map(([tone, label]) => (
        <span key={tone} className="flex items-center gap-1.5">
          <span aria-hidden className={cn("inline-block size-3 rounded-sm", TONE_CLASS[tone])} />
          {label}
        </span>
      ))}
      <span className="ml-auto">
        {note === "filtered"
          ? "진한 열 = 필터에 맞는 실행 · 기준 열은 비교 기준이라 항상 진하게 둔다"
          : "행 = 고정 TC · 열 = 실행한 TE · 셀 클릭 → Test Result"}
      </span>
    </div>
  );
}

/**
 * The TC behind a row. Enough to recognize it and a way to the rest
 * (All Test Cases' drawer) — not a second copy of that drawer.
 */
function TcPanel({
  api,
  testCaseId,
  inPage,
  pageReady,
  holding,
  onClose,
  onShowPage,
}: {
  api: AdminApi;
  testCaseId: string;
  inPage: BatchPageTestCase | null;
  pageReady: boolean;
  holding: readonly string[] | null;
  onClose: () => void;
  onShowPage: (pageId: string) => void;
}) {
  const navigate = useNavigate();
  const [handing, setHanding] = useState(false);
  const [error, setError] = useState("");
  const [tc, setTc] = useState<TestCaseDetail | null>(null);
  const alive = useRef(true);
  useEffect(
    () => () => {
      alive.current = false;
    },
    [],
  );

  // The page row carries only what the Matrix needs; how many 👎 and when it
  // was promoted come from the TC itself (artifact TC panel).
  useEffect(() => {
    let current = true;
    setTc(null);
    api
      .getTestCase(testCaseId)
      .then((found) => current && setTc(found))
      .catch(() => undefined);
    return () => {
      current = false;
    };
  }, [api, testCaseId]);

  // Until the TC itself arrives nothing is known about it, and the button
  // stays as it always was; the server refuses what it does not allow.
  const batchGate = tc ? testCaseGate(tc, "add_to_batch") : null;

  const testIt = async () => {
    setHanding(true);
    setError("");
    try {
      const page = await api.createBatchPage({ mode: "single", test_case_ids: [testCaseId] });
      // Server first, then replace — the same hand-off as All Test Cases (hub §4.3).
      navigate({ to: "/test-cases/batch/$pageId", params: { pageId: page.page_id }, replace: true });
    } catch (err) {
      if (alive.current) {
        setError(errorText(err));
        setHanding(false);
      }
    }
  };

  return (
    <Card className="grid gap-0 overflow-hidden lg:sticky lg:top-5">
      <div className="flex items-center gap-2 border-b border-border px-4 py-3">
        <span className="truncate text-sm font-semibold" title={testCaseId}>
          {tcShort(testCaseId)}
        </span>
        <span className="text-xs text-muted-foreground">TC 기본 정보</span>
        <span className="ml-auto">
          <IconButton label="닫기" onClick={onClose}>
            <X />
          </IconButton>
        </span>
      </div>
      <div className="grid gap-3 p-4 text-[13px]">
        {error && <Banner tone="error">{error}</Banner>}
        {!pageReady ? (
          <Skeleton className="h-16" />
        ) : inPage ? (
          <>
            <div className="rounded-lg border border-l-2 border-border border-l-primary bg-surface-subtle p-3">
              <span className="mb-1 block text-[11px] tracking-widest text-muted-foreground uppercase">
                Query
              </span>
              <p className="whitespace-pre-wrap">{inPage.trigger_preview || inPage.title || "—"}</p>
            </div>
            <dl className="grid grid-cols-[88px_minmax(0,1fr)] gap-x-3 gap-y-2">
              <dt className="text-[11px] text-muted-foreground uppercase">피드백 항목</dt>
              <dd className="flex flex-wrap gap-1">
                {inPage.categories.length === 0
                  ? "—"
                  : inPage.categories.map((c) => (
                      <Badge key={c} size="compact">
                        {c}
                      </Badge>
                    ))}
              </dd>
              <dt className="text-[11px] text-muted-foreground uppercase">도메인</dt>
              <dd className="text-muted-foreground">BE-DOMAIN 대기</dd>
              <dt className="text-[11px] text-muted-foreground uppercase">환경</dt>
              <dd>{inPage.source_environment}</dd>
              <dt className="text-[11px] text-muted-foreground uppercase">세션 유형</dt>
              <dd>{tc?.session_type ? sessionTypeLabel(tc.session_type) : "—"}</dd>
              <dt className="text-[11px] text-muted-foreground uppercase">상태</dt>
              <dd>{inPage.is_active ? "활성" : "은퇴"}</dd>
              <dt className="text-[11px] text-muted-foreground uppercase">연결된 👎</dt>
              <dd className="tabular-nums">{tc ? feedbackCountText(tc.feedback_count) : "—"}</dd>
              <dt className="text-[11px] text-muted-foreground uppercase">승격</dt>
              <dd>{tc ? `${compactTime(tc.promoted_at)} · ${tc.promoted_by ?? "—"}` : "—"}</dd>
            </dl>
          </>
        ) : holding && holding.length > 0 ? (
          <div className="grid gap-2">
            <p className="text-muted-foreground">지금 보이는 Batch Test 에는 이 TC 가 없습니다.</p>
            <Button size="sm" variant="outline" onClick={() => onShowPage(holding[0])}>
              이 TC 가 든 Batch Test 로
            </Button>
          </div>
        ) : (
          <div className="grid gap-2">
            <p className="text-muted-foreground">이 TC 로 실행한 Batch Test 가 없습니다.</p>
            <Button
              size="sm"
              variant="outline"
              disabled={handing || (batchGate !== null && !batchGate.allowed)}
              title={batchGate?.reason ?? undefined}
              onClick={() => void testIt()}
            >
              {handing && <Loader2 className="animate-spin" />}이 TC 로 Test하기
            </Button>
            {batchGate && !batchGate.allowed && (
              <p className="text-[12px] text-muted-foreground">{batchGate.reason}</p>
            )}
          </div>
        )}
        <div className="border-t border-border pt-3">
          <Button
            size="sm"
            variant="outline"
            onClick={() =>
              navigate({
                to: ALL_TEST_CASES_ROUTE,
                search: { ...lastSearch(ALL_TEST_CASES_ROUTE, ALL_TEST_CASES_DEFAULT_SEARCH), tc: testCaseId },
              })
            }
          >
            All Test Cases 에서 전체 보기
          </Button>
        </div>
      </div>
    </Card>
  );
}
